/**
 * Floor annotation property hooks
 *
 * Thin React wrapper around the framework-agnostic getters in
 * `libs/potree-shared/src/integration/annotations/FloorPropertyGetters.ts`.
 *
 * Both reads and writes go through the reactive property service. Writes use the
 * service's setValue/batchUpdateProperties, which persist via the project property
 * manager's GraphQL batch mutation AND broadcast the change to other clients over
 * Pusher (client-properties-batch-updated). This mirrors SurfaceProperties and is
 * what makes floor edits/creation sync live in the multi-user experience.
 */

import { useCallback, useEffect } from 'react';
import { PropertyType } from '../../types';
// Import directly to avoid pulling in pusher.ts via barrel index (breaks Potree build)
import { useReactivePropertyManager } from '../../reactive/useReactivePropertyManager';
import { ReactiveProperty } from '../../reactive/ReactiveProperty';
import { PropertyValueData } from '@pointorama/potree-shared';
import {
  registerFloorProperties,
  getFloorPlanePositionProperty,
  getFloorPlaneNormalProperty,
  getCeilingPlanePositionProperty,
  getCeilingPlaneNormalProperty,
  getFloorFootprintCornerProperty,
} from '@pointorama/potree-shared';

// Re-export the getters so existing client imports keep working.
export {
  registerFloorProperties,
  getFloorPlanePositionProperty,
  getFloorPlaneNormalProperty,
  getCeilingPlanePositionProperty,
  getCeilingPlaneNormalProperty,
} from '@pointorama/potree-shared';

type Coordinate = { x: number; y: number; z: number };

function isCoordinate(value: unknown): value is Coordinate {
  return !!value && typeof value === 'object' && 'x' in value && 'y' in value && 'z' in value;
}

export const useFloorProperties = (projectId?: string) => {
  // Subscribing to the reactive manager registers the broadcast callbacks
  // (onImmediateChange / onImmediateChangeBatch) used to sync writes to peers.
  const service = useReactivePropertyManager({ projectId: projectId || '' });

  // Ensure properties are registered
  useEffect(() => {
    registerFloorProperties();
  }, []);

  const getFloorPlanePosition = useCallback(
    (floorId: string): Coordinate | undefined => {
      const value = getFloorPlanePositionProperty(floorId).value;
      return isCoordinate(value) ? value : undefined;
    },
    [],
  );

  const getFloorPlaneNormal = useCallback(
    (floorId: string): Coordinate | undefined => {
      const value = getFloorPlaneNormalProperty(floorId).value;
      return isCoordinate(value) ? value : undefined;
    },
    [],
  );

  const getCeilingPlanePosition = useCallback(
    (floorId: string): Coordinate | undefined => {
      const value = getCeilingPlanePositionProperty(floorId).value;
      return isCoordinate(value) ? value : undefined;
    },
    [],
  );

  const getCeilingPlaneNormal = useCallback(
    (floorId: string): Coordinate | undefined => {
      const value = getCeilingPlaneNormalProperty(floorId).value;
      return isCoordinate(value) ? value : undefined;
    },
    [],
  );

  // Single-property write — used by the elevation editor in the right panel.
  // Goes through the reactive service so it persists AND broadcasts to peers.
  const setCoordinateProperty = useCallback(
    async (floorId: string, identifier: string, value: Coordinate): Promise<void> => {
      service.setValue(identifier, floorId, PropertyType.PROJECT_GLOBAL, value);
    },
    [service],
  );

  const setFloorPlanePosition = useCallback((floorId: string, position: Coordinate) => setCoordinateProperty(floorId, 'floor_plane_position', position), [setCoordinateProperty]);
  const setFloorPlaneNormal = useCallback((floorId: string, normal: Coordinate) => setCoordinateProperty(floorId, 'floor_plane_normal', normal), [setCoordinateProperty]);
  const setCeilingPlanePosition = useCallback((floorId: string, position: Coordinate) => setCoordinateProperty(floorId, 'ceiling_plane_position', position), [setCoordinateProperty]);
  const setCeilingPlaneNormal = useCallback((floorId: string, normal: Coordinate) => setCoordinateProperty(floorId, 'ceiling_plane_normal', normal), [setCoordinateProperty]);

  // Batch update — used when creating a floor. Writes all planes in one reactive
  // batch so the values land in the local service immediately, persist via the
  // GraphQL batch mutation, and fire a single client-properties-batch-updated
  // event to other clients.
  const setFloorPropertiesBatch = useCallback(
    async (
      floorId: string,
      properties: {
        floorPlanePosition?: Coordinate;
        floorPlaneNormal?: Coordinate;
        ceilingPlanePosition?: Coordinate;
        ceilingPlaneNormal?: Coordinate;
        /** The floor's own XY footprint (4 corners; z carries the floor elevation). */
        footprintCorners?: Coordinate[];
      },
    ): Promise<void> => {
      type PropertyPair = [ReactiveProperty<PropertyValueData>, PropertyValueData];
      const propertyValuePairs: PropertyPair[] = [];

      if (properties.floorPlanePosition) {
        propertyValuePairs.push([getFloorPlanePositionProperty(floorId) as unknown as ReactiveProperty<PropertyValueData>, properties.floorPlanePosition]);
      }
      if (properties.floorPlaneNormal) {
        propertyValuePairs.push([getFloorPlaneNormalProperty(floorId) as unknown as ReactiveProperty<PropertyValueData>, properties.floorPlaneNormal]);
      }
      if (properties.ceilingPlanePosition) {
        propertyValuePairs.push([getCeilingPlanePositionProperty(floorId) as unknown as ReactiveProperty<PropertyValueData>, properties.ceilingPlanePosition]);
      }
      if (properties.ceilingPlaneNormal) {
        propertyValuePairs.push([getCeilingPlaneNormalProperty(floorId) as unknown as ReactiveProperty<PropertyValueData>, properties.ceilingPlaneNormal]);
      }
      if (properties.footprintCorners && properties.footprintCorners.length >= 4) {
        properties.footprintCorners.slice(0, 4).forEach((corner, i) => {
          propertyValuePairs.push([getFloorFootprintCornerProperty(floorId, i) as unknown as ReactiveProperty<PropertyValueData>, corner]);
        });
      }

      if (propertyValuePairs.length > 0) {
        await service.batchUpdateProperties(propertyValuePairs);
      }
    },
    [service],
  );

  return {
    getFloorPlanePosition,
    setFloorPlanePosition,
    getFloorPlaneNormal,
    setFloorPlaneNormal,
    getCeilingPlanePosition,
    setCeilingPlanePosition,
    getCeilingPlaneNormal,
    setCeilingPlaneNormal,
    setFloorPropertiesBatch,
  };
};
